import { isEmpty, isNil } from 'ramda';
import React, { forwardRef, useMemo, useRef } from 'react';
import { useClickAway } from 'react-use';
import { Icon } from '../icon';
import { StyledTableTop } from '../table/components/table-top/styled';
import { TableLayout } from '../table/table-layout';
import { Body2 } from '../typography';
import { DataGridActions } from './components/data-grid-actions';
import { SettingsContainer, SidebarDirectoryContainer, TableIsEmpty, Wrapper, WrapperTable } from './styled';
/**
 * `DataGrid` is a component that displays a data grid with various features.
 * It is a wrapper around the `TableContainer` component and provides additional functionality.
 *
 * @component
 * @example
 * ```tsx
 * <DataGrid
 *   tableInstance={tableInstance}
 *   actionBlockIcon={actionBlockIcon}
 *   errorComponent={errorComponent}
 *   className="my-class"
 *   tabs={tabs}
 *   totalItems={100}
 *   pagination={pagination}
 *   actionBar={actionBar}
 *   topSubtitle={topSubtitle}
 *   columnSettings={columnSettings}
 *   searchBlock={searchBlock}
 *   sidebarDirectory={sidebarDirectory}
 *   isSidebarDirectoryShown={true}
 *   dataGridProps={dataGridProps}
 *   needOverflowTabs={true}
 *   searchParameters={searchParameters}
 * />
 * ```
 *
 * @param {Object} props - The properties that define the `DataGrid` component.
 * @param {TableInstance} props.tableInstance - The instance of the table.
 * @param {JSX.Element} [props.actionBlockIcon] - The icon for the action block.
 * @param {JSX.Element} [props.errorComponent] - The component to display in case of an error.
 * @param {JSX.Element} [props.noDataComponent] - A JSX element that is rendered when there is no data to display.
 * @param {string} [props.className] - The CSS class name of the component.
 * @param {JSX.Element} [props.tabs] - The tabs to display in the data grid.
 * @param {number} props.totalItems - The total number of items in the data grid.
 * @param {JSX.Element} [props.pagination] - The pagination component.
 * @param {JSX.Element} [props.actionBar] - The action bar component.
 * @param {JSX.Element} [props.topSubtitle] - The subtitle to display at the top of the data grid.
 * @param {JSX.Element} [props.columnSettings] - The settings for the columns in the data grid.
 * @param {JSX.Element} [props.searchBlock] - The search block component.
 * @param {JSX.Element} [props.sidebarDirectory] - The sidebar directory component.
 * @param {boolean} [props.isSidebarDirectoryShown] - Whether the sidebar directory is shown.
 * @param {DataGridPropsHookT} props.dataGridProps - The properties for the data grid hook.
 * @param {boolean} [props.needOverflowTabs] - Whether overflow tabs are needed.
 * @param {Record<string, any>} [props.searchParameters] - The search parameters.
 *
 * @returns {JSX.Element} The `DataGrid` component.
 **/
export const DataGrid = forwardRef(function TableContainer({ tableInstance, actionBlockIcon, errorComponent, noDataComponent, className, tabs, totalItems, pagination, actionBar, topSubtitle, columnSettings, searchBlock, sidebarDirectory, isSidebarDirectoryShown, onRowClick, top, needOverflowTabs, dataGridProps: { activeSidebar, toggleSideMenu, layoutType }, ...rest }, ref) {
    var _a;
    const isSettingsOpen = Boolean(activeSidebar.length);
    const isShowActionBlock = useMemo(() => [tabs, pagination, actionBar, columnSettings].some((element) => Boolean(element)), [tabs, pagination, columnSettings, actionBar]);
    // TODO - saved searches implementation
    /* const elements = [
        { type: 'saved', value: ['foo', 'bar'] },
        { type: 'saved', value: ['foo', 'bar'] }
      ]; */
    const settingsRef = useRef(null);
    const buttonOpenRef = useRef(null);
    useClickAway(settingsRef, (event) => {
        const { current: el } = buttonOpenRef;
        const isOpenButton = el === null || el === void 0 ? void 0 : el.contains(event.target);
        if (activeSidebar.length && !isOpenButton) {
            toggleSideMenu('tableSetting')();
        }
    }, ['click']);
    return (React.createElement(Wrapper, { ref: ref, className: className },
        isSidebarDirectoryShown && (React.createElement(SidebarDirectoryContainer, null, sidebarDirectory ? sidebarDirectory : null)),
        React.createElement(WrapperTable, { "$withSidebarDirectory": Boolean(isSidebarDirectoryShown) },
            !Boolean(searchBlock) && !isShowActionBlock ? null : (React.createElement(DataGridActions, { ref: buttonOpenRef, isSettingsOpen: isSettingsOpen, searchBlock: searchBlock, activeSidebar: activeSidebar, tabs: tabs, pagination: pagination, actionBar: actionBar, topSubtitle: topSubtitle, columnSettings: columnSettings, actionBlockIcon: actionBlockIcon, toggleSideMenu: toggleSideMenu, needOverflowTabs: needOverflowTabs })),
            !isNil(top) && React.createElement(StyledTableTop, null, top),
            !rest.isLoading &&
                !noDataComponent &&
                tableInstance.rows.length === 0 &&
                (((_a = tableInstance.state.globalFilter) === null || _a === void 0 ? void 0 : _a.length) > 0 ||
                    (!isNil(rest.searchParameters) && !isEmpty(rest.searchParameters))) ? (React.createElement(TableIsEmpty, null,
                React.createElement(Icon, { name: "dissatisfied", size: 30 }),
                React.createElement(Body2, null, "No records match your search."),
                React.createElement(Body2, null, "Try modifying search criteria."))) : (React.createElement(TableLayout, { layoutType: layoutType, tableInstance: tableInstance, additionalProps: rest.additionalProps, onRowClick: onRowClick, bottom: rest.bottom, hasTop: isShowActionBlock || !isNil(searchBlock) || !isNil(top), renderRowSubComponent: rest.renderRowSubComponent, errorComponent: errorComponent, noDataComponent: noDataComponent, isLoading: rest.isLoading })),
            React.createElement(SettingsContainer, { "$active": isSettingsOpen, ref: settingsRef }, columnSettings && activeSidebar === 'tableSetting' ? columnSettings : undefined))));
});
